import { brandCssVars } from "./palette";
import type { BrandThemeConfig } from "./types";

// applyTheme.ts — pushes a resolved brand onto the document.
//
// Three things live outside React and so can't be handed to a provider: the
// CSS custom properties every Tailwind utility resolves through, the favicon,
// and the browser-chrome theme colour. This writes all three.
//
// For a production build the ACTIVE brand's tokens are also inlined into
// index.html by the brand plugin in vite.config.ts, so the first paint is
// already correct and this call is a confirming no-op. It is the only
// application path in dev, in jsdom tests, and for any future runtime source.

export function applyBrandTheme(theme: BrandThemeConfig, doc: Document = document): void {
  const root = doc.documentElement;

  // Tags the document with the active brand. Also what the build-time token
  // block in index.html is keyed on (`:root[data-brand="…"]`).
  root.setAttribute("data-brand", theme.id);

  // Inline custom properties outrank any stylesheet, so a brand switch never
  // half-applies against the tokens.css fallback block.
  for (const [name, value] of Object.entries(brandCssVars(theme.colors))) {
    root.style.setProperty(name, value);
  }

  applyFavicon(theme, doc);
  applyThemeColor(theme, doc);
}

function applyFavicon(theme: BrandThemeConfig, doc: Document): void {
  const { href, type } = theme.assets.favicon;
  let link = doc.head.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = doc.createElement("link");
    link.rel = "icon";
    doc.head.appendChild(link);
  }
  link.setAttribute("href", href);
  link.setAttribute("type", type);
}

function applyThemeColor(theme: BrandThemeConfig, doc: Document): void {
  let meta = doc.head.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = doc.createElement("meta");
    meta.name = "theme-color";
    doc.head.appendChild(meta);
  }
  meta.setAttribute("content", theme.colors.bg);
}
